import React, { useCallback, useMemo } from 'react';
import BaseModalComponent from '../BaseModal.component';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import FormLayoutComponent from '../../Form/FormLayout.component';
import SelectComponent from '../../Input/Select.component';
import ButtonComponent, { StyledButton } from '../../Button.component';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import DatepickerModalComponent from '../DatepickerModal.component';
import dayjs from 'dayjs';
import {
  DATE_TIME_FORMAT,
  TIME_PICKER_STEP,
} from '../../../constants/date.contant';
import InputComponent from '../../Input/Input.component';
import useNotificationHook from '../../../hooks/useNotification.hook';
import { saveNotificationThunk } from '../../../redux/Notification/NotificationThunk';
import { NotificationRaw } from '../../../redux/Notification/NotificationType';
import ApiWrapper from '../../../hooks/useApi.hook';
import useFormHook from '../../../hooks/useForm.hook';
import { required } from '../../../services/validator.service';

interface NotificationModalProps extends NotificationRaw {
  onFinally?: Function;
}

const StyledNewNotificationModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0 0 0;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;
const StyledNewNotificationModal = styled.div``;

const NewNotificationModalComponent = ({
  targetType,
  targetId,
  onFinally,
}: NotificationModalProps) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModalManager();
  const { getSelectTypes: getNotificationTypes } = useNotificationHook();
  const nextWorkDay = useMemo(() => {
    return dayjs().add(1, 'hour');
  }, []);

  const { form, registerField, validate, handleFormChange } = useFormHook({
    type: undefined,
    date: nextWorkDay.unix() * 1000,
  });

  const saveNotification = useCallback(() => {
    if (!validate()) return;

    ApiWrapper(
      saveNotificationThunk({
        targetType,
        targetId,
        // @ts-ignore
        type: form.type!.value,
        sendAt: form.date! / 1000,
      }),
    ).then((res: boolean) => {
      if (!res) return;

      closeModal();
      onFinally && onFinally();
    });
  }, [form, targetType, targetId, onFinally]);

  const renderHeader = useMemo(() => {
    return t('New notification');
  }, []);

  const renderFooter = useMemo(() => {
    return (
      <StyledNewNotificationModalFooter>
        <ButtonComponent
          appearance="primary"
          disabled={
            form.date <= dayjs().add(TIME_PICKER_STEP, 'minutes').unix() * 1000
          }
          handleClick={() => saveNotification()}
        >
          {t('Next')}
        </ButtonComponent>

        <ButtonComponent handleClick={closeModal}>
          {t('Cancel')}
        </ButtonComponent>
      </StyledNewNotificationModalFooter>
    );
  }, [form]);

  return (
    <StyledNewNotificationModal>
      <BaseModalComponent header={renderHeader} footer={renderFooter}>
        <FormLayoutComponent>
          <SelectComponent
            {...registerField('type', {
              validations: [required],
            })}
            value={form.type}
            label={t('Type') + '*'}
            placeholder={t('Choose type')}
            loadOptions={getNotificationTypes}
            onChangeOption={(type: any) => {
              handleFormChange({ type });
            }}
            isSearchable={false}
            useMenuPortalTarget
          />
          <InputComponent
            {...registerField('date')}
            value={dayjs(form.date).format(DATE_TIME_FORMAT)}
            label={t('Date')}
            onClick={() =>
              openModal(DatepickerModalComponent, {
                defaultDate: form.date,
                handleChange: (date: number) => {
                  handleFormChange({ date });
                },
              })
            }
          />
        </FormLayoutComponent>
      </BaseModalComponent>
    </StyledNewNotificationModal>
  );
};

export default NewNotificationModalComponent;
